/**
 * A risk acceptance the control assistant proposed after the IT lead said the firm accepted a gap or a risk
 * (Andres, 2026-09-25). The IT lead checks the wording, enters who decided, and records it through the
 * existing risk acceptance route; nothing is created until they click. A member of the firm's executive
 * group then approves it, on the control page or in the risk register.
 */
import { FAMILY_LINK_LABEL } from '@grc/shared/vocabulary';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, useApiClient } from '../api';
import { formatDate } from '../components';
import { suggestedBecause } from './PlanProposal';

export interface ProposedAcceptance {
  controlId: string;
  risk: { id: number; code: string; title: string; linkedByFamily?: boolean } | null;
  accepted: string;
  rationale: string;
  compensatingControls: string;
  reviewDueOn: string;
  approverRole: string;
  critical: boolean;
  /** 5D: the IT lead asked for it, or the conversation held the detail. */
  basis?: 'asked' | 'conversation';
}

const oneYearFrom = (iso: string) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCFullYear(d.getUTCFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

export function AcceptanceProposal({ firmCode, proposal, canAdd, onDone, onRecorded, supersedes = null }: {
  firmCode: string;
  proposal: ProposedAcceptance;
  canAdd: boolean;
  onDone: () => void;
  /** Called once the acceptance is recorded, so the page can show it with the firm's other decisions. */
  onRecorded?: () => void;
  /** The control's accepted decision, which a decision recorded on the control replaces (CP-05), as on the control page's own form. */
  supersedes?: number | null;
}) {
  const call = useApiClient();
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({
    rationale: proposal.rationale,
    compensatingControls: proposal.compensatingControls,
    approverName: '',
    approverRole: proposal.approverRole,
    decidedOn: today,
    reviewDueOn: proposal.reviewDueOn,
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recorded, setRecorded] = useState(false);
  // DEC-40: a risk tied to this control by the CDW family fallback says so, in the same words as Leadership.
  const subject = proposal.risk ? `risk ${proposal.risk.code}, ${proposal.risk.title}${proposal.risk.linkedByFamily ? ` (${FAMILY_LINK_LABEL.charAt(0).toLowerCase()}${FAMILY_LINK_LABEL.slice(1)})` : ''}` : `${proposal.controlId.startsWith('E8-') ? 'Essential Eight' : 'CIS'} ${proposal.controlId}`;
  const where = proposal.risk ? `/firm/${firmCode}/risks` : `/firm/${firmCode}/controls/${proposal.controlId}#acceptances`;

  if (recorded) {
    return (
      <p className="assistant-note acceptance-recorded" role="status">
        Recorded. It now awaits approval from a member of your firm's executive group.{' '}
        <Link to={where}>{proposal.risk ? 'They approve it in the risk register' : 'They approve it on this control page'}</Link>.
      </p>
    );
  }

  const ready = f.rationale.trim().length >= 20 && f.approverName.trim() && f.approverRole.trim() && f.decidedOn && f.reviewDueOn;
  const record = async () => {
    setBusy(true);
    setError(null);
    try {
      const path = proposal.risk ? `/firms/${firmCode}/risks/${proposal.risk.id}/risk-acceptances` : `/firms/${firmCode}/controls/${proposal.controlId}/risk-acceptances`;
      await call(path, {
        method: 'POST',
        body: {
          rationale: f.rationale.trim(),
          compensatingControls: f.compensatingControls.trim() || null,
          approverName: f.approverName.trim(),
          approverRole: f.approverRole.trim(),
          decidedOn: f.decidedOn,
          reviewDueOn: f.reviewDueOn,
          ...(proposal.risk ? {} : { supersedes }),
        },
      });
      setRecorded(true);
      onRecorded?.();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The risk acceptance was not recorded.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="proposal plan-proposal acceptance-proposal" aria-label="Record this as a risk acceptance?">
      <p className="proposal-kicker">Suggestion from the assistant. Not recorded yet.</p>
      <h3>Record this as a risk acceptance?</h3>
      <p className="proposal-why small">
        {suggestedBecause(proposal.basis, 'a risk acceptance')} {canAdd ? 'Nothing is recorded until you click Record for approval.' : "Nothing is recorded unless your firm's IT lead records it."}
      </p>
      <p className="small">
        {/* NEXT PASS 3: plain phrasing in place of a middle-dot pair. */}
        What would be accepted, on {subject}: <strong>{proposal.accepted}</strong>
      </p>
      <p className="small">A risk acceptance puts your firm's decision and its reason on file. It explains the control's status; a member of your executive group approves it.</p>
      {canAdd ? (
        <>
          <label>
            Why the firm accepts this
            <textarea rows={4} minLength={20} maxLength={4000} value={f.rationale} onChange={set('rationale')} />
          </label>
          <label>
            Compensating controls
            <textarea rows={2} maxLength={4000} value={f.compensatingControls} onChange={set('compensatingControls')} />
          </label>
          <div className="row">
            <label>
              Decided by
              <input required maxLength={200} value={f.approverName} onChange={set('approverName')} />
            </label>
            <label>
              Their role
              <input required maxLength={120} value={f.approverRole} onChange={set('approverRole')} />
            </label>
          </div>
          <div className="row">
            <label>
              Decision date
              <input type="date" required max={today} value={f.decidedOn} onChange={set('decidedOn')} />
            </label>
            <label>
              Review by (within a year)
              <input type="date" required min={f.decidedOn} max={oneYearFrom(f.decidedOn || today)} value={f.reviewDueOn} onChange={set('reviewDueOn')} />
            </label>
          </div>
          <p className="small">
            Suggested review: {formatDate(proposal.reviewDueOn)}
            {proposal.critical ? ', sooner than a year because this control is rated Critical.' : '.'}
          </p>
          <div className="actions">
            <button type="button" className="pill" disabled={busy || !ready} onClick={() => void record()}>
              {busy ? 'Recording…' : 'Record for approval'}
            </button>
            <button type="button" className="pill secondary-dark" disabled={busy} onClick={onDone}>
              Not now
            </button>
          </div>
        </>
      ) : (
        <p className="assistant-note">Your firm's IT lead can record this as a risk acceptance; a member of the executive group then approves it.</p>
      )}
      {error && (
        <p role="alert" className="assistant-error">
          {error}
        </p>
      )}
    </section>
  );
}
